import { useModalResponsiveTableRequestContext } from '@/components/DashboardCard/components/ModalResponsiveTableRequestV2';
import { useTabsViewsContext } from '@/components/DashboardCard/components/ResponsiveTableV2/components/TabsViews';
import { maskItem } from '@/components/DashboardCard/utils/maskItem';
import { CheckIcon } from '@chakra-ui/icons';
import { Box, Spinner, Text } from '@chakra-ui/react';
import React, { lazy, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PhotoModal from '@/components/DashboardCard/components/PhotoModal';
import { FiMinus, FiPlus } from 'react-icons/fi';

import { CellTypeProps } from '../../interfaces';
import { CellItem, Tooltip, ValueArea } from './styles';

const PopoverComponent = lazy(() => import('@/components_v2/PopoverComponent'));

const Default: React.FC<CellTypeProps> = ({
  color,
  backgroundColor,
  textAlign,
  children,
  extraValue,
  value,
  custom_tooltip,
  fontWeight,
  maskValue,
  fontSize,
  cellStyle,
  modalSubComponent,
  activeAccordion,
  cellClicked,
}) => {
  const { activeTabs, handleSelectedItems, loading } = useTabsViewsContext();
  const parsedValue = maskValue ? maskItem(maskValue, value) : value;
  const customTooltip = maskValue
    ? maskItem(maskValue, custom_tooltip)
    : custom_tooltip;

  const contentRef = useRef(null);
  const { handleOpen } = useModalResponsiveTableRequestContext();
  const activeTab = activeTabs.get(cellClicked?.column?.id);

  const activeTabSelected = activeTab?.selected?.find((item) => {
    const sameId = item?.expandId
      ? cellClicked?.uniqueId?.includes(item.expandId) ?? false
      : item?.uniqueId && cellClicked?.uniqueId
        ? cellClicked.uniqueId.includes(item.uniqueId)
        : true;
    const sameValue = item.value === value;

    return sameValue && sameId;
  });

  const { t } = useTranslation();

  const isValidUrl = (string: string) => {
    try {
      const url = new URL(string);
      return url.origin !== "null";
    } catch (_) {
      return false;
    }
  };

  const isImageUrl = (url: string) => {
    return /\.(jpeg|jpg|gif|png|svg|webp)(\?.*)?$/i.test(url);
  };

  const getBooleanValue = (value: any) => {
    if (typeof value === 'boolean' && value) {
      return <CheckIcon color="green.400" w={6} h={6} />;
    }

    if (typeof value === 'string' && isValidUrl(value)) {
      if (isImageUrl(value)) {
        return (
          <PhotoModal
            photoURL={value}
            trigger={
              <Text
                as="span"
                cursor="pointer"
                textDecoration="underline"
                color="blue.500"
              >
                {t('common.open_image')}
              </Text>
            }
          />
        );
      }
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'blue', textDecoration: 'underline' }}
        >
          {t('common.open_link')}
        </a>
      );
    }

    return value;
  };

  const isNotTotal = value !== 'TOTAL';
  const isNotEmptyValue = value !== '-';

  const renderCellItem = () => (
    <CellItem
      fontWeight={fontWeight}
      backgroundColor={backgroundColor || 'transparent'}
      color={color || '#333'}
      style={cellStyle}
    >
      <ValueArea
        textAlign={textAlign || 'right'}
        onClick={() => {
          if (modalSubComponent) {
            handleOpen(modalSubComponent);
          }
          if (activeAccordion && parsedValue && !loading && isNotTotal && isNotEmptyValue) {
            handleSelectedItems({
              data: cellClicked,
              checked: !!activeTabSelected,
            });
          }
        }}
      >
        {activeAccordion &&
          isNotTotal &&
          parsedValue &&
          isNotEmptyValue &&
          (activeTabSelected ? (
            <FiMinus />
          ) : (
            <FiPlus style={{ marginRight: '0.10rem' }} />
          ))}
        <Box
          maxHeight="100%"
          width="100%"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          overflow="hidden"
          backgroundColor={backgroundColor}
          fontSize={fontSize ?? 10}
          ref={contentRef}
          textDecoration="none"
        >
          {getBooleanValue(parsedValue)}
          {children !== '-' && extraValue}
        </Box>
        {activeAccordion && parsedValue && loading && <Spinner size="sm" />}
      </ValueArea>
    </CellItem>
  );

  if (!customTooltip && !parsedValue && !extraValue) {
    return renderCellItem();
  }

  if (
    customTooltip?.length <= 8 ||
    parsedValue?.length <= 8 ||
    extraValue?.toString()?.length <= 8 ||
    value?.toString()?.length <= 8
  ) {
    return renderCellItem();
  }

  return (
    <PopoverComponent placement="top" trigger={renderCellItem()}>
      <Tooltip>
        {custom_tooltip ? customTooltip : parsedValue}
        {children !== '-' && extraValue}
      </Tooltip>
    </PopoverComponent>
  );
};

export default Default;
